import { ChangeEvent } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ExpenseCategory } from "../../types/daily-expense.type";
import { AMOUNT_ERROR_TEXT, UNIT_TEXT } from "./constants";

const FIELD_MIN_WIDTH = 260;
const FIELD_MAX_WIDTH = 320;
const AMOUNT_PLACEHOLDER = "0.00";

interface AdminAmountFieldProps {
  category: ExpenseCategory;
  value: string;
  hasError: boolean;
  readOnly: boolean;
  onChange: (categoryCode: string, text: string) => void;
  onBlur: (categoryCode: string) => void;
}

function AdminAmountField({
  category,
  value,
  hasError,
  readOnly,
  onChange,
  onBlur,
}: Readonly<AdminAmountFieldProps>) {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange(category.code, event.target.value);
  const handleBlur = () => onBlur(category.code);

  return (
    <Stack gap={0.75}>
      <Typography variant="body2">{category.name}</Typography>
      <TextField
        size="small"
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        error={hasError}
        helperText={hasError ? AMOUNT_ERROR_TEXT : undefined}
        placeholder={AMOUNT_PLACEHOLDER}
        slotProps={{
          htmlInput: {
            "aria-label": category.name,
            inputMode: "decimal",
            readOnly,
            style: { textAlign: "right" },
          },
        }}
        sx={{ "& .MuiInputBase-root": { bgcolor: readOnly ? "action.hover" : "background.paper" } }}
      />
    </Stack>
  );
}

interface AdminExpenseFieldsProps {
  fields: ExpenseCategory[];
  values: Record<string, string>;
  /** Category codes to outline in red; empty until the user tries to save. */
  errorCodes: Set<string>;
  readOnly: boolean;
  onChange: (categoryCode: string, text: string) => void;
  onBlur: (categoryCode: string) => void;
}

/** ส่วนที่ 2 · ฝ่ายบริหาร: the branch-wide amounts (GM fuel, GM tolls). */
export default function AdminExpenseFields({
  fields,
  values,
  errorCodes,
  readOnly,
  onChange,
  onBlur,
}: Readonly<AdminExpenseFieldsProps>) {
  return (
    <Paper>
      <Stack direction="row" alignItems="baseline" gap={1.25} mb={1.5}>
        <Typography fontWeight={600}>2 · ฝ่ายบริหาร</Typography>
        <Typography variant="body2" color="text.secondary">
          ทั้งสาขา · {UNIT_TEXT}
        </Typography>
      </Stack>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(auto-fill, minmax(${FIELD_MIN_WIDTH}px, ${FIELD_MAX_WIDTH}px))`,
          gap: 2,
        }}
      >
        {fields.map((category) => (
          <AdminAmountField
            key={category.code}
            category={category}
            value={values[category.code] ?? ""}
            hasError={errorCodes.has(category.code)}
            readOnly={readOnly}
            onChange={onChange}
            onBlur={onBlur}
          />
        ))}
      </Box>
    </Paper>
  );
}
